import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { Button, Checkbox, typography } from '@landsbankinn/ui';
import { useAcceptedCookies } from '@landsbankinn/utils';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { animated, useSpring } from 'react-spring';
import { Config } from 'lib/config';
import { FacebookPixel } from 'lib/facebook-pixel';
import res from './CookieConsent.res';

const Overlay = styled(animated.div)<{ hidden?: boolean }>`
  position: fixed;
  z-index: 101;
  bottom: 32px;
  left: 32px;
  width: 320px;
  padding: 32px;
  background: white;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  overflow: hidden;
  .no-js & {
    display: none;
  }
  ${(props) => (props.hidden ? 'display:none' : '')};
`;

const Heading = styled.div`
  ${typography.h4}
  margin-bottom: 8px;
`;

const Checkboxes = styled.div`
  margin-bottom: 16px;
`;

const Item = styled.div`
  margin-bottom: 8px;
  label {
    font-size: 16px;
    line-height: 2.1 !important;
  }
`;

const Description = styled.p`
  font-size: 18px;
  margin-top: 8px;
`;

const Copy = styled.p`
  font-size: 18px;
  margin-bottom: 16px;
`;

const AcceptButton = styled(Button)`
  height: 48px;
  width: 100%;
  margin-top: 8px;
`;

const ConfigureButton = styled.button`
  margin-top: 0;
  height: 48px;
  font-size: 18px;
  display: block;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: #f6f6f6;
  font-weight: 400;
  text-align: center;
  cursor: pointer;
  transition: background-color ease-in-out 275ms;
  &:hover {
    background-color: #e6e6e6;
  }
`;

const CookieTypes = [
  {
    key: 'REQUIRED',
    title: res.essentialCookies,
    description: res.ensureWebsiteFunctionality,
    disabled: true,
  },
  {
    key: 'FUNCTIONAL',
    title: res.practicalCookies,
    description: res.practicalCookiesDetails,
  },
  {
    key: 'ADVERTISING',
    title: res.marketingCookies,
    description: res.marketingCookiesDetails,
  },
];

const TRACKING_DEBUG = false;

const Slider = styled(animated.div)`
  display: block;
`;

const Slide = styled(animated.div)<any>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const Container = styled(animated.div)`
  position: relative;
  overflow: hidden;
  width: 100%;
  padding: 32px;
`;

export function CookieConsent() {
  const { r } = useResource();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(400);
  const [configure, setConfigure] = useState(false);
  const [ariaHidden, setAriaHidden] = useState(false);
  const accepted = useAcceptedCookies();
  const [hidden, setHidden] = useState(false);
  const [selected, setSelected] = useState({
    REQUIRED: true,
    FUNCTIONAL: false,
    ADVERTISING: false,
  });

  const overlay = useSpring({
    delay: 330,
    from: { opacity: 0, y: 10 },
    to: async (next) => {
      await next({
        opacity: accepted.accepted ? 0 : 1,
        y: accepted.accepted ? 10 : 0,
      }),
        await next({
          x: accepted.accepted ? -500 : 0,
        });
    },
    onRest: () => {
      !accepted.accepted ? setHidden(false) : setHidden(true);
    },
  });

  const a = useSpring({
    opacity: configure ? 1 : 0,
    pointerEvents: configure ? 'all' : 'none',
  });

  const b = useSpring({
    opacity: !configure ? 1 : 0,
    pointerEvents: !configure ? 'all' : 'none',
  });

  const container = useSpring({
    height,
  });

  const onConfigure = () => {
    setConfigure((v) => !v);
  };

  const onSubmit = () => {
    accepted.setAcceptedCookies(selected);
  };

  const onAcceptAll = () => {
    const all = Object.keys(selected).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    accepted.setAcceptedCookies(all);
  };

  useEffect(() => {
    if (accepted.accepted) {
      if (accepted.REQUIRED) {
        // required cookies
      }
      if (accepted.FUNCTIONAL) {
        const gtag = (window as any).gtag;
        if (typeof window !== 'undefined') {
          const options = {
            ad_storage: accepted.ADVERTISING ? 'granted' : 'denied',
            analytics_storage: 'granted',
          };
          if (document.getElementById('gtag')) {
            gtag('consent', 'update', options);
          } else {
            TagManager.initialize({ gtmId: Config.GTM_ID });
          }
        }
      }
      if (accepted.ADVERTISING) {
        if (!accepted.FUNCTIONAL) {
          TagManager.initialize({ gtmId: Config.GTM_ID ?? '' });
        }
        if (Config.FACEBOOK_PIXEL) {
          FacebookPixel.init(Config.FACEBOOK_PIXEL, undefined, {
            debug: TRACKING_DEBUG,
            autoConfig: true,
          });
        }
      }
    }
    setAriaHidden(accepted.accepted);
  }, [accepted.accepted]);

  useEffect(() => {
    if (sliderRef.current) {
      const rect =
        sliderRef.current.children[configure ? 1 : 0].getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [configure]);

  const personuvernd = (
    <Copy>
      <Link href="https://www.landsbankinn.is/bankinn/um-bankann/personuvernd">
        <a style={{ borderBottom: '1px dotted #e5e5e5' }}>
          {r(res.moreOnCookies)}
        </a>
      </Link>
    </Copy>
  );

  return (
    <Overlay style={overlay} aria-hidden={ariaHidden} hidden={hidden}>
      <Heading>{configure ? r(res.select) : r(res.cookies)}</Heading>
      <Container style={container}>
        <Slider ref={sliderRef}>
          <Slide style={b}>
            <Copy>{r(res.byClicking)}</Copy>
            {personuvernd}
            <ConfigureButton onClick={onConfigure}>
              {r(res.select)}
            </ConfigureButton>
            <AcceptButton
              bg={(props: any) => props.theme.colors.primary}
              title={r(res.allowAll)}
              onClick={onAcceptAll}
              aria-hidden={configure}
            />
          </Slide>
          <Slide style={a} aria-hidden={!configure}>
            <Checkboxes>
              {CookieTypes.map((item) => (
                <Item key={item.key}>
                  <Checkbox
                    label={r(item.title)}
                    id={`COOKIE_${item.key}`}
                    checked={selected[item.key] === true}
                    disabled={item.disabled}
                    variant="noBorder"
                    onChange={(value) => {
                      setSelected((values) => ({
                        ...values,
                        [item.key]: value,
                      }));
                    }}
                  />
                  {item.description && (
                    <Description>{r(item.description)}</Description>
                  )}
                </Item>
              ))}
            </Checkboxes>
            {personuvernd}
            <ConfigureButton onClick={onConfigure}>
              {r(res.cancel)}
            </ConfigureButton>
            <AcceptButton
              bg={(props: any) => props.theme.colors.primary}
              title={r(res.save)}
              onClick={onSubmit}
            />
          </Slide>
        </Slider>
      </Container>
    </Overlay>
  );
}
